import React, { useEffect, useRef, useState } from 'react'
import { distanceMeasure } from '../Utils/distanceCalculator.js'
import { addSavedMeasurement, measureColor, measureInfoText, readSavedMeasurements } from '../Utils/savedMeasurements.js'
import { useClickStore, useInforStore } from '../store/clickStore.js'
import { useShallow } from 'zustand/react/shallow'
import { Line, Sphere } from '@react-three/drei'

function toTriple(p) {
  if (!p) return null;
  if (Array.isArray(p)) return p;
  return [p.x, p.y, p.z];
}

function pairKey(p1, p2) {
  const a = toTriple(p1);
  const b = toTriple(p2);
  if (!a || !b) return '';
  return `${a[0]},${a[1]},${a[2]}|${b[0]},${b[1]},${b[2]}`;
}

function MeasureSegment({ m, selected, onSelect }) {
  const a = toTriple(m.p1);
  const b = toTriple(m.p2);
  if (!a || !b) return null;
  const color = measureColor(m);
  return (
    <group
      userData={{ mdvSkipClip: true, mdvMeasureId: m.id, mdvSkipPick: false }}
      onClick={(e) => {
        e.stopPropagation();
        onSelect?.();
      }}
    >
      <Line
        points={[a, b]}
        color={color}
        lineWidth={selected ? 4 : 2.4}
        dashed
        dashScale={12}
        dashSize={2}
        gapSize={1.4}
      />
      <Sphere args={[selected ? 2.4 : 1.7, 8, 8]} position={a}>
        <meshBasicMaterial color={color} />
      </Sphere>
      <Sphere args={[selected ? 2.4 : 1.7, 8, 8]} position={b}>
        <meshBasicMaterial color={color} />
      </Sphere>
    </group>
  );
}

export default function MeasureLines() {
    const { clickType, measure_points, selectedMeasureId, setSelectedMeasureId } = useClickStore(useShallow((state) => ({
        clickType: state.clickType,
        measure_points: state.measure_points,
        selectedMeasureId: state.selectedMeasureId,
        setSelectedMeasureId: state.setSelectedMeasureId,
    })))
    const lastPairRef = useRef('');
    const [saved, setSaved] = useState(() => readSavedMeasurements());

    const { setInfo } = useInforStore(useShallow((state) => ({
        setInfo: state.setInfo
    })))

    useEffect(() => {
      const onChange = () => setSaved(readSavedMeasurements());
      window.addEventListener('mdv-measurements-changed', onChange);
      return () => window.removeEventListener('mdv-measurements-changed', onChange);
    }, []);

    useEffect(() => {
        const measure_results = distanceMeasure(measure_points)
        if (measure_results) {
            const key = `${measure_points[0].x},${measure_points[0].y},${measure_points[0].z}|${measure_points[1].x},${measure_points[1].y},${measure_points[1].z}`;
            if (key !== lastPairRef.current) {
              lastPairRef.current = key;
              const next = addSavedMeasurement({
                a: measure_results.a,
                d: measure_results.d,
                dy: measure_results.dy,
                dist: measure_results.dist,
                p1: { x: measure_points[0].x, y: measure_points[0].y, z: measure_points[0].z },
                p2: { x: measure_points[1].x, y: measure_points[1].y, z: measure_points[1].z },
              });
              const created = next[0];
              if (created) setSelectedMeasureId(created.id);
            }
        }
    }, [measure_points, setSelectedMeasureId])

    const selected = saved.find((m) => m.id === selectedMeasureId);
    useEffect(() => {
      if (!selected) return;
      setInfo(measureInfoText(selected));
    }, [selected, setInfo]);

    const activeKey = measure_points.length === 2 ? pairKey(measure_points[0], measure_points[1]) : '';

    return (
        <group>
            {saved.map((m) => (
              <MeasureSegment
                key={m.id}
                m={m}
                selected={m.id === selectedMeasureId || (clickType === 'distance' && pairKey(m.p1, m.p2) === activeKey)}
                onSelect={() => setSelectedMeasureId(m.id)}
              />
            ))}
            {clickType === 'distance' && measure_points.length === 2 && (
              <Line
                points={measure_points}
                color="#ffffff"
                lineWidth={2}
                dashed
                dashScale={10}
                dashSize={1}
                gapSize={1}
              />
            )}
            {clickType === 'distance' && measure_points[0] && (
              <Sphere args={[2.2, 8, 8]} position={measure_points[0]}>
                <meshBasicMaterial color="hotpink" />
              </Sphere>
            )}
            {clickType === 'distance' && measure_points[1] && (
              <Sphere args={[2.2, 8, 8]} position={measure_points[1]}>
                <meshBasicMaterial color="hotpink" />
              </Sphere>
            )}
        </group>
    )
}
